import { useTranslation } from 'react-i18next';
import { FaEnvelope, FaMapPin, FaPhone } from 'react-icons/fa';
import { Link, useLocation } from 'react-router-dom';

import clutchImg from '@/assets/icons/clutch.svg';
import facebookImg from '@/assets/icons/facebook.svg';
import githubImg from '@/assets/icons/github.svg';
import linkedinImg from '@/assets/icons/linkedin.svg';
import logoFooterImg from '@/assets/logo_footer.svg';
import { ButtonScrollTop } from '@/components/ButtonScrollTop';
import { Map } from '@/components/GoogleMap';
import './MainLayout.css';
import LanguagePicker from '@/components/LanguagePicker';
import { serviceItems } from '@/data/service-items';
import { translate } from '@/translations';

interface Props {
  mainRef: React.RefObject<HTMLElement>;
}

export const Footer = (props: Props) => {
  const { t } = useTranslation('navigation');
  const location = useLocation();

  const getLinkClass = (path: string) => {
    return location.pathname === path ? 'text-button font-bold' : 'hover:text-button';
  };

  return (
    <>
      <footer className="w-full duration-200 z-20">
        <div>
          <div className="bg-black w-full h-full px-5 md:px-20 text-white text-xs md:text-lg">
            <div className="w-full pt-5 flex-">
              <div className="w-full pb-9">
                <div className="flex justify-center items-center pt-7">
                  <img src={logoFooterImg} alt={translate('images:logo')} loading="lazy" />
                </div>

                <div className="grid grid-cols-3 gap-1 xl:flex xl:justify-center xl:items-start w-full h-fit pt-7">
                  <div className="flex flex-col justify-start items-center w-auto xl:w-64">
                    <Link to="/" className={`py-3 uppercase ${getLinkClass('/')}`}>
                      {t('home')}
                    </Link>
                  </div>

                  <div className="flex flex-col justify-start items-center w-auto xl:w-64">
                    <Link to="/services" className={`py-3 uppercase ${getLinkClass('/services')}`}>
                      {t('services')}
                    </Link>
                    <ul className="hidden sm:block text-gray-600 mb-6">
                      {serviceItems.map((service, index) => (
                        <li className="text-sm py-1  hover:text-hoverButton" key={index}>
                          <Link
                            to={{ pathname: '/services', hash: service.id }}
                            reloadDocument={location.pathname === '/services'}
                          >
                            {translate(service.titleKey)}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="flex flex-col justify-start items-center w-auto xl:w-64">
                    <Link
                      to="/who-we-are"
                      className={`py-3 uppercase ${getLinkClass('/who-we-are')}`}
                    >
                      {t('whoWeAre')}
                    </Link>
                  </div>

                  <div className="flex flex-col justify-start items-center w-auto xl:w-64">
                    <Link
                      to="/portfolio"
                      className={`py-3 uppercase ${getLinkClass('/portfolio')}`}
                    >
                      {t('portfolio')}
                    </Link>
                  </div>

                  <div className="flex flex-col justify-start  items-center w-auto xl:w-64">
                    <Link to="/contact" className={`py-3 uppercase ${getLinkClass('/contact')}`}>
                      {t('contact')}
                    </Link>
                  </div>
                </div>

                <div className="flex items-center justify-center mt-8">
                  <Link
                    to="/privacy-policy"
                    className={`py-1 uppercase text-xs ${getLinkClass('/privacy-policy')}`}
                  >
                    {t('privacyPolicy')}
                  </Link>
                </div>
                <div className="flex items-center justify-center text-xs my-8">
                  <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-y-2 sm:gap-y-4 gap-x-16">
                    <div className="mt-4 sm:mt-0">{translate('company:nip')}:</div>
                    <div>7543368340</div>

                    <div className="mt-4 sm:mt-0">{translate('company:krs')}:</div>
                    <div>0001101111</div>

                    <div className="mt-4 sm:mt-0">{translate('company:regon')}:</div>
                    <div>528398086</div>
                  </div>
                </div>

                <div className="sm:relative flex justify-center">
                  <div className="sm:absolute sm:-bottom-48 lg:-bottom-32 2xl:-bottom-32 grid justify-center lg:grid lg:grid-cols-3 lg:divide-x w-2/3 lg:w-3/4 py-3 2xl:py-6 rounded-lg bg-black opacity-90">
                    <div className=" flex items-center sm:justify-center w-full ">
                      <FaPhone className="text-white w-6 h-6" />
                      <a
                        href="tel:+48500570440"
                        className="pl-2 py-3 text-base xl:text-lg hover:text-button"
                      >
                        +48 500 570 440
                      </a>
                    </div>
                    <div className="flex items-center sm:justify-center w-full ">
                      <FaMapPin className="text-white w-6 h-6" />
                      <a
                        href="https://maps.app.goo.gl/7i6ZPQZL2s4V7dg16"
                        target="_blank"
                        rel="noopener noreferrer"
                        className=" pl-2 py-3 text-base xl:text-lg hover:text-button"
                      >
                        ul. Technologiczna 2, 45-839 Opole
                      </a>
                    </div>
                    <div className="flex items-center sm:justify-center w-full ">
                      <FaEnvelope className="text-white w-6 h-6" />
                      <a
                        href="mailto:contact@intechnity.com"
                        className="pl-2 py-3 text-base xl:text-lg hover:text-button"
                      >
                        contact@intechnity.com
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="w-full h-[70vh]">{/* Placeholder for map content */}</div>

          <div className="flex justify-between items-center w-full footer px-5 bg-black fixed bottom-0 -z-1 text-xs">
            <div className="flex justify-start items-center gap-8">
              <ButtonScrollTop mainRef={props.mainRef} />
              <p className="text-white">Intechnity Sp. z o.o.. All rights reserved. 2025</p>
            </div>
            <div className="flex text-white justify-between items-center gap-8">
              <div className="hidden md:block">
                <a href="https://clutch.co/profile/intechnity" target="_blank" rel="noreferrer">
                  <img src={clutchImg} alt="Clutch Intechnity" loading="lazy" className="h-6" />
                </a>
              </div>
              <div className="hidden md:block">
                <a href="https://www.facebook.com/intechnitycom" target="_blank" rel="noreferrer">
                  <img src={facebookImg} alt="Facebook Intechnity" loading="lazy" className="h-6" />
                </a>
              </div>
              <div className="hidden md:block">
                <a href="https://github.com/Intechnity-com" target="_blank" rel="noreferrer">
                  <img src={githubImg} alt="GitHub Intechnity" loading="lazy" className="h-6" />
                </a>
              </div>
              <div className="hidden md:block">
                <a
                  href="https://www.linkedin.com/showcase/intechnitycom/"
                  target="_blank"
                  rel="noreferrer"
                >
                  <img src={linkedinImg} alt="LinkedIn Intechnity" loading="lazy" className="h-6" />
                </a>
              </div>
              <div>
                <LanguagePicker />
              </div>
            </div>
          </div>
        </div>
      </footer>

      <Map />
    </>
  );
};
